/* eslint-disable @typescript-eslint/naming-convention */
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { HotToastService } from '@ngneat/hot-toast';
import { BadgeOptions } from 'app/components/badge/badge.interface';
import { DataCollectorService } from 'app/core/services/data_collector_services/data_collector_service.service';
import { LocalisationService } from 'app/core/services/localisation/zuper.localisation.service';
import { UtilityService } from 'app/core/services/utility_functions/utility_functions.service';
import { PickerSelectOptions, PickerSelectDisableOptions } from '../data-picker.interface';
import { UntypedFormGroup } from '@angular/forms';
import _ from 'lodash';
import { TimezonePickerService } from 'app/core/services/timezone/timezone-picker.service';

@Component({
  selector: 'customer-data-picker',
  templateUrl: './customer-data-picker.component.html',
  styleUrls: ['./customer-data-picker.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CustomerDataPickerComponent implements OnInit, OnChanges {
  @Input() filter: any = null;
  @Input() selectOption: PickerSelectOptions = PickerSelectOptions.select;
  @Output() eventEmitter: EventEmitter<any> = new EventEmitter();
  @Input() selectedCustomers: any[] = [];
  @Input() searchKey: string = '';
  @Input() pickerConfig: any = null;
  @Input() customerUrl: any = '';
  @Input() options: any = null;
  @Input() pageIndex: number = 0;
  @Input() showSelectedItems: boolean = false;
  @Input() sortBy: string = 'customer_first_name';
  @Input() sortType: string = 'ASC';
  @Input() customerData: any[] = null;
  @Input() customParams: any = null;
  @Input() filterFormGroup: UntypedFormGroup;
  @Input() hasFilterValue: boolean = false;
  @Input() selectionDisable: { selectionDisableBasedOn: PickerSelectDisableOptions; elementId?: string; tooltipText?: string } = null; //NA for readonly selectOption
  @Input() maxSelectionLimit: number = null; // only applicable for multiselect

  duplicatedCustomerData: any[] = [];
  customers: any[] = [];
  customersList: any[] = [];
  customerEleId: string = '';
  customerDetails: any = null;
  loaded: boolean = false;
  onSearch: boolean = true;
  localeKeys: any = {
    expand: 'expand',
    collapse: 'collapse'
  };
  defaultParamObj: any = {
    'sort': this.sortType,
    'sort_by': this.sortBy,
    'filter.is_active': true
  };
  isSpinLoading: boolean = false;
  isMaxSelectionLimit: boolean = false;

  constructor(
    private utilityService: UtilityService,
    private dataCollectorService: DataCollectorService,
    private localisationService: LocalisationService,
    private changeDetectorRef: ChangeDetectorRef,
    private timezoneService: TimezonePickerService,
    private toast: HotToastService) {
  }

  ngOnInit(): void {
    this.localisationService.setDefaultLanguage();
    if (this.selectOption && this.selectOption === PickerSelectOptions.readOnly) {
      delete this.defaultParamObj?.['filter.is_active'];
    }
    this.filterFormGroup?.valueChanges.subscribe((data: any) => {
      this.updateDataParams(data);
    });
    this.showDataPickerPanel();
  }

  validateSelectionDisable(customer: any): boolean {
    if (this.selectionDisable?.selectionDisableBasedOn === PickerSelectDisableOptions.CUSTOMER && customer?.customer_uid === this.selectionDisable?.elementId) {
      return true;
    }
    return false;
  }

  rotateArrowIcon(customerUID: any): any {
    return {
      'rotate-arrow-icon-down': this.isExpandingAction(customerUID),
      'rotate-arrow-icon-up': !this.isExpandingAction(customerUID)
    };
  }

  showSpinLoading(customerUID: any): boolean {
    return this.isExpandingAction(customerUID) && this.isSpinLoading;
  }

  updateArrowTooltipText(customerUID: any): string {
    return this.isExpandingAction(customerUID) ? this.localeKeys.collapse : this.localeKeys.expand;
  }

  isExpandingAction(customerUID: any): boolean {
    return (this.customerEleId === 'customer_details_' + customerUID);
  }

  viewCustomerFullDetails(customerUID: string): string {
    return '/customers/' + customerUID + '/details';
  }

  getStatusBadgeOptions(type: string, displayName: string): BadgeOptions {
    const badgeOption: BadgeOptions = {
      type: type,
      displayType: type,
      displayKey: null,
      displayName: displayName ?? type
    };
    return badgeOption;
  }

  viewCustomerDetails(event: any, customerUID: string): void {
    event.stopPropagation();
    const elementId: string = 'customer_details_' + customerUID;
    if (this.customerEleId === elementId) {
      this.resetCustomerDetails();
    } else {
      this.customerEleId = elementId;
      this.getCustomerDetails(customerUID);
    }
  }

  getCustomerDetails(customerUID: string): void {
    this.isSpinLoading = true;
    /* eslint-disable quotes */
    this.dataCollectorService.getData(this.customerUrl + `/` + customerUID).subscribe(
      /* eslint-enable quotes */
      (customers: any) => {
        this.customerDetails = customers.data;
        if(this.customerDetails.customer_timezone){
          const tz: string = this.timezoneService.getSelectedTimeZone(this.customerDetails.customer_timezone);
          this.customerDetails = {...this.customerDetails,customer_timezone_value: tz};
        }
        this.isSpinLoading = false;
        this.changeDetectorRef.detectChanges();
      }, (error: any) => {
        this.resetCustomerDetails();
        this.isSpinLoading = false;
      });
  }

  resetCustomerDetails(): void {
    this.customerEleId = '';
    this.customerDetails = null;
  }

  isSelectedCustomer(customer: any): boolean {
    if (this.selectedCustomers?.length) {
      return this.selectedCustomers.find(value => value.customer_uid === customer.customer_uid) ? true : false;
    }
    return false;
  }

  selectCustomer(event: any, customer: any): void {
    if (this.selectOption === PickerSelectOptions.multiSelect) {
      if (event?.target?.checked === true) {
        this.selectedCustomers.push(customer);
      } else {
        this.selectedCustomers = this.selectedCustomers.filter(value => value.customer_uid !== customer.customer_uid);
      }
    } else {
      this.selectedCustomers = [customer];
    }
    if (this.maxSelectionLimit && this.selectedCustomers.length > this.maxSelectionLimit) {
      // If MaxSelectionLimit is reached, emit the submit button, disabling
      this.isMaxSelectionLimit = true;
      this.eventEmitter.emit({ maxSelectReached: true });
    }
    else {
      this.isMaxSelectionLimit = false;
      this.eventEmitter.emit({ maxSelectReached: false });
    }
    this.eventEmitter.emit({ value: this.selectedCustomers });
  }

  triggerSearch(searchKey: any): void {
    this.onSearch = true;
    if (this.customerData) {
      this.customerDataSearch(searchKey);
    } else {
      this.customerRemoteDataSearch(searchKey);
    }
  }

  customerDataSearch(searchKey: any): void {
    if (!searchKey) {
      this.customers = this.duplicatedCustomerData;
    } else {
      const filteredValue: any[] = this.duplicatedCustomerData?.filter((customer: any) => {
        searchKey = searchKey.toLowerCase();
        const customerFirstName: string = customer?.customer_first_name?.toLowerCase();
        const customerLastName: string = customer?.customer_last_name?.toLowerCase();
        const customerEmail: string = customer?.customer_email?.toLowerCase();
        if (customerFirstName?.indexOf(searchKey) > -1 || customerLastName?.indexOf(searchKey) > -1 || customerEmail?.indexOf(searchKey) > -1) {
          return customer;
        }
      });
      this.customers = filteredValue?.length ? [...filteredValue] : [];
    }
  }

  customerRemoteDataSearch(searchKey: any): void {
    delete this.defaultParamObj[this.pickerConfig.params.uniqueSearchKey];
    if (this.defaultParamObj[this.pickerConfig.params.searchKey] !== searchKey) {
      const pageIndex: number = 0;
      this.defaultParamObj[this.pickerConfig.params.searchKey] = searchKey;
      this.getData(pageIndex);
      this.eventEmitter.emit({ pageIndex: pageIndex });
    }
  }

  sortingAction(key: string, value: string): void {
    if (value) {
      const pageIndex: number = this.pageIndex??0;
      this.defaultParamObj[key] = value;
      this.getData(pageIndex);
      this.eventEmitter.emit({ pageIndex: pageIndex });
    }
  }

  getData(pageIndex: number, isOpen?: boolean): void {
    this.customerEleId = '';
    this.loaded = false;
    this.defaultParamObj.page = pageIndex + 1;
    
    if (this.defaultParamObj['filter.organization'] || this.defaultParamObj['filter.customer_organization']) {
      if (this.defaultParamObj['filter.organization']) {
        this.defaultParamObj['filter.customer_organization'] = this.defaultParamObj['filter.organization'];
        delete this.defaultParamObj['filter.organization'];
      }
      delete this.defaultParamObj['filter.has_org'];
    } else if (this.options?.filters?.getCustomerWithoutOrg) {
      this.defaultParamObj['filter.has_org'] = false;
    }
    const urlParams = this.utilityService.constructParam(this.defaultParamObj);

    this.dataCollectorService.getData(this.customerUrl, urlParams).subscribe(
      (response: any) => {
        this.customersList = response.data;
        this.customers = response.data;
        this.loaded = true;
        this.eventEmitter.emit({ totalRecords: response.total_records });
        if (isOpen && this.filter && this.customers.length) {
          this.selectedCustomers = this.selectedCustomers?.length > 0 ? _.uniqBy([...this.selectedCustomers, ...this.filter], 'customer_uid') : this.filter;
          this.eventEmitter.emit({ value: this.selectedCustomers });
        } else if (isOpen && this.customers.length === 0) {
          this.onSearch = false;
        }
        this.changeDetectorRef.detectChanges();
      }, (error: any) => {
        this.loaded = true;
      });
  }

  showDataPickerContent(): void {
    if (this.hasFilterValue) {
      return null;
    }
    const pageIndex: number = this.pageIndex??0;
    Object.keys(this.pickerConfig.params).forEach((key: any) => {
      const paramKey: any = this.pickerConfig.params[key];
      if (key !== 'uniqueSearchKey') {
        let value: any = '';
        if (key === 'countKey') {
          value = 10;
        } else if (key === 'pageKey') {
          value = 1;
        } else if (key === 'searchKey' && !this.showSelectedItems && this.searchKey) {
          value = this.searchKey;
        }
        this.defaultParamObj[paramKey] = value;
      }
    });
    if (this.customParams && Object.keys(this.customParams).length) {
      this.defaultParamObj = { ...this.defaultParamObj, ...this.customParams };
    }
    if (this.filter) {
      this.eventEmitter.emit({ pageIndex: pageIndex });
    }
    this.eventEmitter.emit({ sortBy: this.defaultParamObj['sort_by'], sortType: this.defaultParamObj['sort'] });
    this.getData(pageIndex, true);
  }

  updateDataParams(formControlValues: any): void {
    if (this.customParams && Object.keys(this.customParams).length) {
      this.defaultParamObj = { ...this.defaultParamObj, ...this.customParams };
    }
    const params: any = this.defaultParamObj ?? {};
    if (formControlValues.jobCategory) {
      params['filter.category'] = formControlValues.jobCategory.category_uid;
    } else {
      delete params['filter.category'];
    }

    if (formControlValues.moduleBasedFilterValue) {
      formControlValues.moduleBasedFilterValue.forEach((value: any) => {
        if (value.value) {
          params[value?.key] = value.value;
        } else {
          delete params[value?.key];
        }
      });
    }
    this.updateFilters();
  }

  updateFilters(): void {
    const pageIndex: number = 0;
    this.getData(pageIndex, true);
    this.eventEmitter.emit({ pageIndex: pageIndex });
  }

  updateCustomerData(): void {
    this.customers = this.customerData;
    this.duplicatedCustomerData = [...this.customerData];
    this.onSearch = false;
    this.loaded = true;
    if (this.filter && this.customerData?.length) {
      this.selectedCustomers = this.filter;
      this.eventEmitter.emit({ value: this.selectedCustomers });
    }
    if (this.searchKey) {
      this.eventEmitter.emit({ searchKey: this.searchKey });
      this.triggerSearch(this.searchKey);
    }
  }

  resetCustomerDataPicker(): void {
    this.customers = [];
  }

  showDataPickerPanel(): void {
    this.resetCustomerDataPicker();
    if (this.customerData) {
      this.updateCustomerData();
    } else if (this.showSelectedItems) {
      this.customers = this.selectedCustomers;
    } else if (this.customersList.length === 0 && this.searchKey) {
      this.showDataPickerContent();
    } else if (this.customersList.length > 0 || this.searchKey) {
      this.customers = this.customersList;
    } else {
      this.showDataPickerContent();
    }
  }

  hideSelectedItemsPanel(): void {
    this.eventEmitter.emit({ showSelectedItems: false, value: this.selectedCustomers, filter: this.selectedCustomers });
  }

  getBadgeOptions(): BadgeOptions {
    return {
      type: 'PRIMARY',
      displayKey: null,
      displayName: this.selectedCustomers ? this.selectedCustomers.length : 0,
      displayType: 'listingCount'
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.searchKey?.firstChange === false) {
      this.triggerSearch(changes?.searchKey?.currentValue);
    }

    if (changes?.pageIndex?.previousValue !== undefined) {
      this.getData(changes?.pageIndex?.currentValue);
    }

    if (changes?.showSelectedItems?.firstChange === false || changes?.customerData?.firstChange === false) {
      this.showDataPickerPanel();
    }

    if (changes?.sortBy?.firstChange === false && changes?.sortBy?.previousValue !== undefined) {
      this.sortingAction('sort_by', changes?.sortBy?.currentValue);
    }

    if (changes?.sortType?.firstChange === false && changes?.sortType?.previousValue !== undefined) {
      this.sortingAction('sort', changes?.sortType?.currentValue);
    }
  }
}
